import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { inputCls, labelCls, btnPrimary, PanelHeader } from "./_shared";

export default function ContactPanel() {
  const { adminApi } = useAuth();
  const { refresh } = useContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/content").then((r) => setForm({ ...r.data.contact })).catch(() => toast.error("Failed to load"));
  }, [adminApi]);

  if (!form) return <div className="text-white/50">Loading…</div>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/content/contact", {
        phone: form.phone,
        phone_link: form.phone_link,
        email: form.email,
        area: form.area,
        review_url: form.review_url || "",
        facebook_url: form.facebook_url || "",
        google_profile_url: form.google_profile_url || "",
        address_street: form.address_street || "",
        address_city: form.address_city || "",
        address_state: form.address_state || "",
        address_postal: form.address_postal || "",
        hours_display: form.hours_display || "",
        hours_open: form.hours_open || "",
        hours_close: form.hours_close || "",
        service_areas: Array.isArray(form.service_areas)
          ? form.service_areas
          : (form.service_areas || "").split(",").map((s) => s.trim()).filter(Boolean),
        google_review_count: Number(form.google_review_count) || 0,
        google_rating: Number(form.google_rating) || 0,
      });
      toast.success("Contact info updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="contact-panel">
      <PanelHeader
        eyebrow="07"
        title="Contact Info"
        description="Phone, email, and service area shown across the site (header, hero, contact section, footer)."
        actions={
          <button data-testid="contact-save" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        }
      />

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>Phone (Display)</label>
          <input className={inputCls} value={form.phone || ""} onChange={(e) => setF("phone", e.target.value)} placeholder="(254) 833-7563" />
        </div>
        <div>
          <label className={labelCls}>Phone (Tel Link — digits only, w/ country)</label>
          <input className={inputCls} value={form.phone_link || ""} onChange={(e) => setF("phone_link", e.target.value)} placeholder="+12548337563" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" className={inputCls} value={form.email || ""} onChange={(e) => setF("email", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Service Area</label>
          <input className={inputCls} value={form.area || ""} onChange={(e) => setF("area", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Facebook Page URL</label>
          <input
            data-testid="contact-facebook-url"
            className={inputCls}
            value={form.facebook_url || ""}
            onChange={(e) => setF("facebook_url", e.target.value)}
            placeholder="https://facebook.com/lcbuildersllc"
          />
          <p className="text-xs text-white/40 mt-2">
            When set, a "Follow on Facebook" button appears in the hero, contact section, and footer.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Google Business Profile URL</label>
          <input
            data-testid="contact-google-profile-url"
            className={inputCls}
            value={form.google_profile_url || ""}
            onChange={(e) => setF("google_profile_url", e.target.value)}
            placeholder="https://share.google/0987BFNNB1vQxMA9S"
          />
          <p className="text-xs text-white/40 mt-2">
            When set, the footer shows a direct link to your Google Business Profile, and it's added to the LocalBusiness JSON-LD schema for SEO.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Google Review Link (used by "Send Review Request" button)</label>
          <input
            data-testid="contact-review-url"
            className={inputCls}
            value={form.review_url || ""}
            onChange={(e) => setF("review_url", e.target.value)}
            placeholder="https://g.page/r/XXXXX/review"
          />
          <p className="text-xs text-white/40 mt-2">
            When set, a "Leave a Google Review" call-to-action button appears at the bottom of the Testimonials section, and past customers can be emailed a one-click review link from the Estimate Leads panel after a job's done.
          </p>
        </div>

        {/* Address — for LocalBusiness JSON-LD schema (helps Google connect the site to your GBP listing) */}
        <div className="md:col-span-2 border-t border-white/10 pt-6">
          <div className="text-xs font-display tracking-widest uppercase text-[#FF6B00] mb-4">
            Physical Address (used in Contact card + LocalBusiness SEO)
          </div>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Street Address</label>
          <input
            data-testid="contact-address-street"
            className={inputCls}
            value={form.address_street || ""}
            onChange={(e) => setF("address_street", e.target.value)}
            placeholder="1568 FM 580 East"
          />
        </div>
        <div>
          <label className={labelCls}>City</label>
          <input
            data-testid="contact-address-city"
            className={inputCls}
            value={form.address_city || ""}
            onChange={(e) => setF("address_city", e.target.value)}
            placeholder="Lampasas"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>State</label>
            <input
              data-testid="contact-address-state"
              className={inputCls}
              value={form.address_state || ""}
              onChange={(e) => setF("address_state", e.target.value)}
              placeholder="TX"
              maxLength={2}
            />
          </div>
          <div>
            <label className={labelCls}>Postal</label>
            <input
              data-testid="contact-address-postal"
              className={inputCls}
              value={form.address_postal || ""}
              onChange={(e) => setF("address_postal", e.target.value)}
              placeholder="76550"
            />
          </div>
        </div>

        {/* Hours */}
        <div className="md:col-span-2 border-t border-white/10 pt-6">
          <div className="text-xs font-display tracking-widest uppercase text-[#FF6B00] mb-4">
            Business Hours
          </div>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Hours (Display Text)</label>
          <input
            data-testid="contact-hours-display"
            className={inputCls}
            value={form.hours_display || ""}
            onChange={(e) => setF("hours_display", e.target.value)}
            placeholder="Open Daily 7:00 AM – 7:00 PM"
          />
          <p className="text-xs text-white/40 mt-2">
            Free-form hours text shown in the Contact card (e.g. "Mon–Sat 7 AM – 7 PM · Sun by appointment").
          </p>
        </div>
        <div>
          <label className={labelCls}>Opens (24h — for SEO)</label>
          <input
            data-testid="contact-hours-open"
            className={inputCls}
            value={form.hours_open || ""}
            onChange={(e) => setF("hours_open", e.target.value)}
            placeholder="07:00"
          />
        </div>
        <div>
          <label className={labelCls}>Closes (24h — for SEO)</label>
          <input
            data-testid="contact-hours-close"
            className={inputCls}
            value={form.hours_close || ""}
            onChange={(e) => setF("hours_close", e.target.value)}
            placeholder="19:00"
          />
        </div>

        {/* Service Areas */}
        <div className="md:col-span-2 border-t border-white/10 pt-6">
          <label className={labelCls}>Service Areas (comma-separated cities — for Local SEO)</label>
          <textarea
            data-testid="contact-service-areas"
            className={`${inputCls} min-h-[80px]`}
            value={
              Array.isArray(form.service_areas)
                ? form.service_areas.join(", ")
                : form.service_areas || ""
            }
            onChange={(e) =>
              setF(
                "service_areas",
                e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
              )
            }
            placeholder="Lampasas, Copperas Cove, Killeen, Temple, Belton, Burnet, Kempner, Waco, Marble Falls, Round Rock, Georgetown"
          />
          <p className="text-xs text-white/40 mt-2">
            Added to the LocalBusiness JSON-LD schema's `areaServed` — Google uses this for local-pack ranking.
          </p>
        </div>

        {/* Google Review Badge — manual override */}
        <div className="md:col-span-2 border-t border-white/10 pt-6">
          <div className="text-xs font-display tracking-widest uppercase text-[#FF6B00] mb-4">
            Google Reviews Badge (shown in Hero)
          </div>
          <p className="text-xs text-white/50 mb-4">
            After you get a new review on Google, come back here and bump the count. The hero shows a "★★★★★ 5.0 · N Google Reviews" badge whenever the count is greater than zero — it also feeds the LocalBusiness aggregateRating for SEO.
          </p>
        </div>
        <div>
          <label className={labelCls}>Google Review Count</label>
          <input
            data-testid="contact-google-review-count"
            type="number"
            min="0"
            className={inputCls}
            value={form.google_review_count ?? 0}
            onChange={(e) => setF("google_review_count", e.target.value)}
            placeholder="12"
          />
        </div>
        <div>
          <label className={labelCls}>Google Rating (0.0 – 5.0)</label>
          <input
            data-testid="contact-google-rating"
            type="number"
            step="0.1"
            min="0"
            max="5"
            className={inputCls}
            value={form.google_rating ?? 5.0}
            onChange={(e) => setF("google_rating", e.target.value)}
            placeholder="5.0"
          />
        </div>
      </div>
    </div>
  );
}
